import {
  Action,
  AppointmentSchedulingType,
} from "@/dummy-data/appointment-list-data-scheduling";
import { AppointmentStatusEnum } from "@/services";
import { COMPONENT, PRIMITIVE } from "@/tokens/design-tokens";

export const APPOINTMENT_STATUS_RECORD: Record<
  AppointmentSchedulingType["status"],
  Action
> = {
  PENDING: "Scheduled",
  CONFIRMED: "Confirmed",
  CANCELLED: "Canceled",
  NO_SHOW: "No Show",
  CHECKED_IN: "Checked In",
  IN_EXAM: "In Exam",
  COMPLETED: "Signed Off",
  INSTANT: "Instant",
};

/**
 * Appointment statuses from which a visit note can be created / completed
 * inside the patient chart.
 *
 * "PENDING" (shown as "Scheduled") is intentionally excluded: the appointment
 * must be checked in before the encounter can be documented or signed off.
 * https://medarch.atlassian.net/browse/ECRH-6027
 */
export const COMPLETE_VISIT_ENABLED_STATUSES: AppointmentStatusEnum[] = [
  // "PENDING",
  "IN_EXAM",
  "CONFIRMED",
  "INSTANT",
];

export const COMPLETE_VISIT_DISABLED_MSG =
  "Visit Note can be created only after the appointment is checked in";

export const VISIT_HISTORY_ACTION = {
  VIEW: "view",
  ADDENDUM: "addendum",
  EDIT: "edit",
  DELETE: "delete",
} as const;

export const VISIT_HISTORY_LABELS = {
  viewVisit: "View Visit",
  addAddendum: "Add addendum",
  completeVisit: "Complete Visit",
  visitNotCompleted: "Visit Not Completed",
  deleteConfirmation: "You want to delete this Appointment",
  addendumAlreadyAdded: "An addendum is already added to this visit",
  addendumRenderingProviderOnly:
    "Only the rendering provider can add an addendum",
  addendumBillingCompleted:
    "Addendum can't be added after billing is completed for this visit.",
} as const;

export const VISIT_BILLED_STATUS = {
  NOT_BILLED: "NOT_BILLED",
  PARTIALLY_BILLED: "PARTIALLY_BILLED",
  BILLED: "BILLED",
} as const;

export type VisitBilledStatus =
  (typeof VISIT_BILLED_STATUS)[keyof typeof VISIT_BILLED_STATUS];

export const ADDENDUM_MAX_LENGTH = 2500;

export const ADDENDUM_LABELS = {
  title: "Add addendum",
  originalSignedBy: "Original signed by",
  on: "on",
  textLabel: "Addendum",
  textPlaceholder: "Enter the addendum",
  textRequired: "Addendum text is required.",
  maxLengthReached: `Maximum ${ADDENDUM_MAX_LENGTH.toLocaleString()} characters reached.`,
  info: "The original note will not change. Once signed, this addendum can't be edited or deleted.",
  cancel: "Cancel",
  signAddendum: "Sign Addendum",
  saveAndClose: "Save and close",
  signature: "Signature",
  signatureRequired: "Signature is required.",
  importSignature: "Import Signature",
  noSavedSignature:
    "No signature found in your profile. Add one in Settings > Profile.",
  clear: "Clear",
  discardTitle: "Discard this addendum?",
  goBack: "Go back",
  discard: "Discard",
} as const;

export const VIEW_VISIT_ENABLED_STATUSES: AppointmentStatusEnum[] = [
  "COMPLETED",
];

export const DEFAULT_VISIT_NOTE_TYPE = "SIMPLE_SOAP_NOTE";

export const APPOINTMENT_STATUS_COLOR: Partial<
  Record<AppointmentStatusEnum, { color: string; bgColor: string }>
> = {
  PENDING: {
    color: PRIMITIVE.color.brandBlue[500],
    bgColor: PRIMITIVE.color.brandBlue[50],
  },
  CONFIRMED: {
    color: COMPONENT.badge.text.appointmentStatus.confirmed,
    bgColor: COMPONENT.badge.bg.appointmentStatus.confirmed,
  },
  INSTANT: {
    color: COMPONENT.badge.text.appointmentStatus.instant,
    bgColor: COMPONENT.badge.bg.appointmentStatus.instant,
  },
  CHECKED_IN: {
    color: COMPONENT.badge.text.appointmentStatus.checkedIn,
    bgColor: COMPONENT.badge.bg.appointmentStatus.checkedIn,
  },
  IN_EXAM: {
    color: COMPONENT.badge.text.appointmentStatus.inExam,
    bgColor: COMPONENT.badge.bg.appointmentStatus.inExam,
  },
  COMPLETED: {
    color: COMPONENT.badge.text.appointmentStatus.signedOff,
    bgColor: COMPONENT.badge.bg.appointmentStatus.signedOff,
  },
  NO_SHOW: {
    color: COMPONENT.badge.text.appointmentStatus.noShow,
    bgColor: COMPONENT.badge.bg.appointmentStatus.noShow,
  },
  CANCELLED: {
    color: COMPONENT.badge.text.appointmentStatus.cancelled,
    bgColor: COMPONENT.badge.bg.appointmentStatus.cancelled,
  },
  SCHEDULED: {
    color: COMPONENT.badge.text.appointmentStatus.scheduled,
    bgColor: COMPONENT.badge.bg.appointmentStatus.scheduled,
  },
};
